/**
 * Dashboard feature hooks
 *
 * React Query hooks for fetching dashboard data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getQuoteAverageApi,
  getQuoteStatsApi,
  getQuoteFollowupsApi,
  getQuoteFollowupsByQuoteId,
  updateQuoteFollowupApi,
  createQuoteFollowupApi,
} from "../api";
import { queryKey } from '../constants';
import { DateRangeType } from '../types';

/**
 * Hook to get quote average data
 */
export const useQuoteAverage = (region?: number) => {
  return useQuery({
    queryKey: [queryKey.getQuoteAverage, region],
    queryFn: () => getQuoteAverageApi(region),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to get quote statistics data
 */
export const useQuoteStats = (dateRange: DateRangeType = "thisweek", region?: number) => {
  return useQuery({
    queryKey: [queryKey.getQuoteStats, dateRange, region],
    queryFn: () => getQuoteStatsApi(dateRange, region),
  });
};

/**
 * Hook to get quote follow-ups
 */
export const useQuoteFollowups = (regionFilter?: string) => {
  return useQuery({
    queryKey: [queryKey.getQuoteFollowups, regionFilter],
    queryFn: () => getQuoteFollowupsApi(regionFilter),
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Hook to get quote follow-ups by quote ID
 */
export const useQuoteFollowupsByQuoteId = (quoteId: string) => {
  return useQuery({
    queryKey: [queryKey.getQuoteFollowups, quoteId],
    queryFn: () => getQuoteFollowupsByQuoteId(quoteId),
    staleTime: 2 * 60 * 1000,
    enabled: !!quoteId,
  });
};

/**
 * Hook to create a quote follow-up
 */
export const useCreateQuoteFollowup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createQuoteFollowupApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteFollowups] });
      queryClient.invalidateQueries({ queryKey: ['QUOTE_MANAGEMENT_GET_QUOTE_DETAIL'] });
    },
  });
};

/**
 * Hook to update a quote follow-up
 */
export const useUpdateQuoteFollowup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateQuoteFollowupApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey.getQuoteFollowups] });
      queryClient.invalidateQueries({ queryKey: ['QUOTE_MANAGEMENT_GET_QUOTE_DETAIL'] });
    },
  });
};
